/**
 * Utility Helper Functions
 */


import { STATUS_LABELS } from './constants';

/**
 * Format date to readable string
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format date and time
 * @param {string|Date} date - Date to format
 * @returns {string} - Formatted date and time string
 */
export const formatDateTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Format percentage
 * @param {number} value - Value to format
 * @returns {string} - Formatted percentage string
 */
export const formatPercentage = (value) => {
  return `${value}%`;
};

export const toTitleCase = (str) => {
  if (!str) return "-";
  return str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

export const isAdminUser = (email) => {
  const { ADMIN_EMAILS } = require('./constants');
  return ADMIN_EMAILS.has(email?.toLowerCase());
};

export const getHiddenFilters = (statusValue) => {
  if (!statusValue || statusValue === "All Status") return [];
  const statuses = statusValue.includes(",")
    ? statusValue.split(",").map((s) => s.trim().toLowerCase().replace(/ /g, "_"))
    : [statusValue.toLowerCase().replace(/ /g, "_")];
  if (statuses.includes("completed") || statuses.includes("incomplete_left")) return [];
  const PERFORMANCE = ["programming", "dbLevel", "aptitude", "communication", "overallStatus"];
  const SKILLS = ["language", "database"];
  if (statuses.every((s) => s === "not_started")) return [...SKILLS, ...PERFORMANCE];
  return PERFORMANCE;
};

export const buildExportRow = (s) => [
  s.student_id||"", s.name||"", s.branch||"", s.year||"", s.semester||"",
  s.language||"", s.database||"", s.status||"", s.programming_level||"",
  s.database_level||"", s.soft_skills_level||"", s.aptitude_level||"",
  s.overall_status||"", s.overall_score!=null?`${s.overall_score}%`:"",
  s.created_at?new Date(s.created_at+"Z").toLocaleString():"",
  s.completed_at?new Date(s.completed_at+"Z").toLocaleString():"",
  s.email||"",
];

export const downloadCSV = (headers, rows, filename) => {
  const csv = [
    headers.join(","),
    ...rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const getFilterLabel = (key, value, { getDepartmentLabel, getYearLabel, getSemesterLabel }) => {
  const labelMap = {
    branch: (v) => getDepartmentLabel(v),
    year: (v) => getYearLabel(v),
    semester: (v) => getSemesterLabel(v),
    status: (v) => STATUS_LABELS[v?.toLowerCase()] || toTitleCase(v),
    language: (v) => toTitleCase(v),
    database: (v) => toTitleCase(v),
  };
  const formatter = labelMap[key];
  if (!formatter) return value;
  if (typeof value === "string" && value.includes(",")) return value.split(",").map((v) => formatter(v.trim())).join(", ");
  return formatter(value);
};